import React, { useState, useEffect } from "react";
import "./Sidebar.css";
import OptimizelyLogo from "../apps/icons/OptimizelyLogo";
import API_URL from "../../apiConfig";
import { TeamMembersService } from "../../services/teamMembers/teamMembersService";
import { UserService } from "../../services/user/userService";
import { UserSubscriptionDisplayModel } from "../../types/user/userSubscriptionDisplayModel";
import PlanDetailsModal from "../planDetailsModal/PlanDetailsModal";
import Loader from "../../components/common/Loader/Loader";
import { useLocation, useNavigate } from "react-router-dom";
import { getUserId } from "../../utils/tokenUtils";

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  hasOptimizelyApp?: boolean;
}
interface AdminType {
  "User ID": number;
  "First Name": string;
  "Last Name": string;
  "Login Email": string;
  Role: string;
}

interface Plan {
  plan_id: number;
  plan_name: string;
  plan_description: string;
}

interface User {
  user_id: number;
  first_name: string;
  last_name: string;
  login_email: string;
  role: string;
  plan_id?: number;
  plan?: Plan | null;
  admin_user_id?: number | null;
}

interface ApiResponse {
  message: string;
  user: User;
  admin: User | null;
  plan: Plan | null;
  teamMembers?: User[];
}

interface TabDef {
  name: string;
  key: string;
  icon?: string;
  activeIcon?: string;
  svgIcon?: React.ReactNode;
}

const Sidebar: React.FC<SidebarProps> = ({ activeTab, setActiveTab, hasOptimizelyApp = false }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState<User | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [userSubscription, setUserSubscription] = useState<
    UserSubscriptionDisplayModel[]
  >([]);
  const handleViewDetails = () => {
    setShowDetails(true);
  };
  const tabs: TabDef[] = [
    {
      name: "Dashboard",
      key: "dashboard",
      icon: "/images/Dashboard.svg",
      activeIcon: "/images/Dashboardcolor.svg",
    },
    {
      name: "Tasks",
      key: "tasks",
      icon: "/images/Taskcolor.svg",
      activeIcon: "/images/Tasks.svg",
    },
    ...(hasOptimizelyApp
      ? [{ name: "Optimizely", key: "optimizely" }]
      : []),
    {
      name: "History",
      key: "taskhistory",
      icon: "/images/History.svg",
      activeIcon: "/images/Taskhistorycolor.svg",
    },
  ];
  const [userId] = useState(getUserId());

  useEffect(() => {
    async function fetchUser() {
      try {
        setLoading(true);
        const response = await TeamMembersService.getUsers(userId);
        const subscriptionInfoResponse = await UserService.getUserSubscription(
          userId
        );
        if (
          subscriptionInfoResponse.status === 200 &&
          subscriptionInfoResponse.data
        ) {
          setUserSubscription(subscriptionInfoResponse.data);
        }

        if (response.status !== 200) {
          throw new Error(`❌ HTTP Error: ${response.status}`);
        }

        const data: ApiResponse = response.data;

        if (data.admin) setAdmin(data.admin);
        if (data.plan) setPlan(data.plan);
        if (data.user) setUser(data.user);
        if (data.teamMembers) setTeamMembers(data.teamMembers);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    }

    if (userId) {
      fetchUser();
    } else {
    }
  }, [userId]);

  const handleLogoClick = () => {
    // Detect base path (admin or user)
    const basePath = location.pathname.startsWith("/admin")
      ? "/admin/dashboard"
      : "/user/dashboard";

    navigate(basePath);
  };
  if (loading) return <Loader />;
  return (
    <aside className="sidebar">
      <img
        src="/images/Helens.svg"
        alt="Helen Logo"
        className="logo"
        onClick={handleLogoClick}
      />

      <div className="tab-container">
        <nav>
          <ul>
            {tabs.map((tab) => (
              <li
                key={tab.key}
                className={`tab-item ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.key === "optimizely" ? (
                  <OptimizelyLogo
                    size={20}
                    className={`tab-icon ${activeTab === tab.key ? "" : "tab-icon-inactive"}`}
                  />
                ) : (
                  <img
                    src={activeTab === tab.key ? tab.activeIcon : tab.icon}
                    alt={`${tab.name} Icon`}
                    className="tab-icon"
                  />
                )}
                <span>{tab.name}</span>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <div className="tabs-two">
        <div className="admin-section">
          <div className="admin-header">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
            </svg>
            <p className="admin-title">My Sponsor</p>
          </div>
          <div className="admin-content">
            <div className="admin-avatar-initials">
              {admin
                ? `${(admin.first_name?.[0] ?? "").toUpperCase()}${(admin.last_name?.[0] ?? "").toUpperCase()}`
                : "--"}
            </div>
            <div className="admin-text">
              <p className="admin-name">
                {admin ? `${admin.first_name} ${admin.last_name}` : "No Sponsor"}
              </p>
              <p className="admin-role">AI Workforce Sponsor</p>
            </div>
          </div>
        </div>
        <div className="plan-info">
          <div className="plan-list">
            {userSubscription && Array.isArray(userSubscription) && (
              <>
                {/* CASE 1: No subscription at all */}
                {userSubscription.length === 0 && (
                  <div className="plan-row">
                    <img
                      src="/images/Plan.svg"
                      alt="Plan Icon"
                      className="plan-icon"
                    />
                    <div className="plan-title">
                      <p className="plan-type">No Active Subscription</p>
                    </div>
                  </div>
                )}

                {/* CASE 2: Subscription exists but none are active → EXPIRED */}
                {userSubscription.length > 0 &&
                  !userSubscription.some((sub) => sub.isActive) && (
                    <div className="plan-row">
                      <img
                        src="/images/Plan.svg"
                        alt="Plan Icon"
                        className="plan-icon"
                      />
                      <div className="plan-title">
                        <p className="plan-name">Subscription Expired</p>
                      </div>
                    </div>
                  )}

                {/* CASE 3: Active subscription exists */}
                {userSubscription
                  .filter((x) => x.isActive)
                  .map((subInfo) => (
                    <div className="plan-row" key={subInfo.id}>
                      <img
                        src="/images/Plan.svg"
                        alt="Plan Icon"
                        className="plan-icon"
                      />
                      <div className="plan-title">
                        <p className="plan-name">
                          {subInfo.planName || "No Plan"}
                        </p>
                      </div>
                    </div>
                  ))}
              </>
            )}
          </div>

          <div className="divider_sidebar"></div>
          <div className="credit-usage">
            <button className="view-details" onClick={handleViewDetails}>
              View Details
            </button>

            {userSubscription && (
              <PlanDetailsModal
                isOpen={showDetails}
                onClose={() => setShowDetails(false)}
                subscriptionInfo={userSubscription}
                isMasterMode={false}
              />
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
